import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getProductImage } from '@/lib/productMedia';
import { normalizeCurrency } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

// ─────────────────────────────────────────────────────────────
// « Vous aimerez aussi » — recommandations 3×3 des pages publiques
// de détail (produit et service). Même langage visuel que les cartes
// des accueils natives : visuel 4/3, dégradé bas, prix en pastille.
// ─────────────────────────────────────────────────────────────

interface ProductRow {
  id: number;
  name: string;
  price: number | null;
  discount_enabled: boolean | null;
  discount_price: number | null;
  currency: string | null;
  media: unknown;
}

interface ServiceRow {
  id: number;
  titre: string;
  prix: number | null;
  prix_promo: number | null;
  product_image: string | null;
  ville: string | null;
  pays: string | null;
}

interface Props {
  kind: 'product' | 'service';
  excludeId: number;
  /** Limite au commerce courant quand on est sur le site d'un marchand. */
  tenantId?: number | null;
  currency: string;
  title?: string;
  limit?: number;
}

function firstServiceImage(value: string | null): string {
  if (!value) return '';
  return value.split('|').filter(Boolean).find((url) => !/\.(mp4|webm|mov)(\?|$)/i.test(url)) || '';
}

export default function RecommendedSection({
  kind,
  excludeId,
  tenantId,
  currency,
  title,
  limit = 6,
}: Props) {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const { display } = useDisplayCurrency(currency);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (kind === 'product') {
          let query = supabase
            .from('product_items')
            .select('id, name, price, discount_enabled, discount_price, currency, media')
            .eq('status', 'active')
            .neq('id', excludeId)
            .order('created_at', { ascending: false })
            .limit(limit);
          if (tenantId != null) query = query.eq('idcommerce', tenantId);
          const { data, error } = await query;
          if (error) throw error;
          if (!cancelled) setProducts((data || []) as ProductRow[]);
        } else {
          let query = supabase
            .from('nospartenairesservices')
            .select('id, titre, prix, prix_promo, product_image, ville, pays')
            .neq('id', excludeId)
            .order('created_at', { ascending: false })
            .limit(limit);
          if (tenantId != null) query = query.eq('idcommerce', tenantId);
          const { data, error } = await query;
          if (error) throw error;
          if (!cancelled) setServices((data || []) as ServiceRow[]);
        }
      } catch {
        if (!cancelled) {
          setProducts([]);
          setServices([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, excludeId, tenantId, limit]);

  const items = kind === 'product' ? products : services;
  if (loading || items.length === 0) return null;

  const sectionTitle = title || 'Vous aimerez aussi';

  return (
    <section className="mt-12 pt-10 border-t border-background-200/70">
      <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
        <div>
          <span className="block w-10 h-[3px] rounded-full bg-accent-500 mb-2.5"></span>
          <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-950">{sectionTitle}</h2>
          <p className="text-xs md:text-sm text-foreground-500 mt-1">
            {kind === 'product'
              ? 'D’autres articles de la même boutique qui pourraient vous plaire.'
              : 'D’autres prestations du même prestataire qui pourraient vous intéresser.'}
          </p>
        </div>
        <Link
          to={kind === 'product' ? '/products' : '/services'}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap no-underline hover:bg-background-200/70 transition-colors"
        >
          Tout voir <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
        {kind === 'product'
          ? products.map((p) => {
              const image = getProductImage(p.media);
              const cur = normalizeCurrency(p.currency || currency);
              const promo = Boolean(p.discount_enabled && p.discount_price != null);
              const price = Number(p.price || 0);
              const effective = promo ? Number(p.discount_price) : price;
              return (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 no-underline transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/80"
                >
                  <div className="relative aspect-[4/3] bg-background-100 overflow-hidden">
                    {image ? (
                      <img
                        src={image}
                        alt={p.name}
                        title={`${p.name} — produit`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-shopping-bag-line text-3xl text-foreground-300"></i>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/5 to-transparent"></div>
                    {promo && (
                      <span className="absolute top-2.5 left-2.5 px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-[10px] font-bold">
                        Promo
                      </span>
                    )}
                    <span className="absolute bottom-2.5 left-2.5 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-bold text-primary-700">
                      {price ? display(effective, cur) : 'Gratuit'}
                    </span>
                  </div>
                  <div className="p-3 md:p-2.5">
                    <h3 className="text-[13px] md:text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {p.name}
                    </h3>
                  </div>
                </Link>
              );
            })
          : services.map((s) => {
              const image = firstServiceImage(s.product_image);
              const promo = s.prix_promo != null ? Number(s.prix_promo) : null;
              const price = Number(s.prix || 0);
              return (
                <Link
                  key={s.id}
                  to={`/service/${s.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 no-underline transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/80"
                >
                  <div className="relative aspect-[4/3] bg-background-100 overflow-hidden">
                    {image ? (
                      <img
                        src={image}
                        alt={s.titre}
                        title={`${s.titre} — service`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-service-line text-3xl text-foreground-300"></i>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/5 to-transparent"></div>
                    <span className="absolute bottom-2.5 left-2.5 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-bold text-primary-700">
                      {display(promo ?? price, currency)}
                    </span>
                  </div>
                  <div className="p-3 md:p-2.5">
                    <h3 className="text-[13px] md:text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                      {s.titre}
                    </h3>
                    {(s.ville || s.pays) && (
                      <p className="mt-1 text-[10px] text-foreground-400 truncate">
                        <i className="ri-map-pin-line mr-1"></i>
                        {[s.ville, s.pays].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
      </div>
    </section>
  );
}